import React, { useCallback } from 'react';
import { ReactComponent as Lab } from 'assets/icons/testKits/testKit.svg';
import { ReactComponent as FileDownloadIcon } from 'assets/icons/file-download.svg';
import { ReactComponent as PastTestKitIcon } from 'assets/icons/pastTestKit.svg';

import { Card } from '../Card';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import ButtonComponent from 'components/button/buttonComponent';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import { openPdfOnMobileSafe } from 'utilities/fileHelpers';

import moment from 'moment/moment';
import { formatDateForAppointment } from 'utilities/functions';
import { usePastTestKitsReports } from 'hooks/api/usePastTestKitsReports';

interface TestKitsProps {
  patientId?: string;
  appointmentId?: string;
}

const PastTestKits: React.FC<TestKitsProps> = ({
  patientId,
  appointmentId,
}) => {
  const { diagnosticReports, loading } = usePastTestKitsReports({
    patientId,
    appointmentId,
  });

  const { fetchFileFromStorage } = useGetFileFromStorage();

  const getDownloadFunction = useCallback(
    async (diagnosticReportId: string) => {
      if (!diagnosticReportId) return;
      const pdfLink = await fetchFileFromStorage(diagnosticReportId, patientId);
      openPdfOnMobileSafe(pdfLink);
    },
    [fetchFileFromStorage, patientId],
  );

  const { data: locale } = useGetComponent({
    componentId: componentIds.TEST_KIT_LABELS,
    locale: 'en',
  });

  if (!patientId || !appointmentId || loading || !diagnosticReports.length) {
    return (
      <Card
        title={'Past test kits results'}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        backgroundColor="bg-white"
        showSeparator
        Icon={() => <PastTestKitIcon className="w-6 h-6" />}
      >
        <p className="text-med-gray-3 font-medium py-4">
          There are no previous test kits available
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 w-full">
      <Card
        title={'Past test kits results'}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        backgroundColor="bg-white"
        showSeparator
        Icon={() => <PastTestKitIcon className="w-6 h-6" />}
      >
        <div
          className="flex flex-col gap-[10px] max-h-[300px] overflow-y-auto w-full bg-base-content px-5 py-2.5 border border-gray-opacity-15 rounded-lg"
          data-testid="previous-appointments"
        >
          <div className="w-full flex flex-col gap-2.5">
            {diagnosticReports.map(
              ({
                providerPdfId,
                patientPdfId,
                patientPdf,
                providerPdf,
                diagnosticReportTestType,
              }) => {
                const { creation } = patientPdf || providerPdf || {};

                return (
                  <div className="flex flex-col bg-white desktop:flex-row justify-between border border-gray-opacity-15 rounded-lg desktop:items-center w-full gap-2.5 py-2.5 px-2.5 pl-5">
                    <div className="flex flex-col desktop:flex-row desktop:items-center gap-2">
                      <div className="flex items-center gap-2">
                        <div className="text-med-gray-3">
                          <Lab className={'w-6 h-6'} />
                        </div>

                        <div className="flex flex-col text-btn-black font-semibold">
                          {locale?.[diagnosticReportTestType] ??
                            diagnosticReportTestType}
                        </div>
                      </div>

                      <div className="text-med-gray-3">
                        {formatDateForAppointment(
                          moment(creation || undefined).toDate(),
                          {
                            simplified: true,
                            withTimeZone: false,
                          },
                        )}
                      </div>
                    </div>

                    <ButtonComponent
                      onClick={() =>
                        getDownloadFunction(
                          (providerPdfId || patientPdfId) as string,
                        )
                      }
                      type={'outlined'}
                      testID={'download-file-button'}
                      Icon={FileDownloadIcon}
                      iconHeight="w-[15px] h-[18px] cursor-pointer fill-current"
                      iconPosition="left"
                      className="flex !border items-center gap-2.5 !text-xs !w-auto"
                    >
                      <span>Results</span>
                    </ButtonComponent>
                  </div>
                );
              },
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PastTestKits;
